// Fichier de configuration de `--provision` : une commande, un fichier.
//
// Aligner six variables d'environnement devant la commande, plus quatre flags,
// est hostile et se prête mal à la relecture. Tout tient désormais dans un
// fichier au format `.env` — jetons ET paramètres — que la CLI sait générer,
// valider et récapituler.
//
// Toute la logique vit ici, pure et testable : `bin/create-app.js` ne garde que
// les entrées/sorties (écriture du fichier, boucle d'attente, confirmation).

import type { ProvisionOptions } from "./plan"

export interface ConfigKey {
	key: string
	/** Requis pour lancer le provisioning. */
	required: boolean
	/** Commentaire imprimé au-dessus de la clé dans le template. */
	comment: string
	/** Valeur d'exemple, en commentaire — jamais pré-remplie. */
	example?: string
	/** Regroupement dans le template. */
	section: string
}

/**
 * Source de vérité unique : le template généré, la validation et le
 * récapitulatif en dérivent tous. Ajouter une clé ici suffit à la faire
 * apparaître partout — c'est ce qui les empêche de diverger.
 *
 * Les JETONS gardent leur nom conventionnel, sans préfixe : un
 * `export GITHUB_TOKEN=…` déjà présent dans le shell doit fonctionner tel quel.
 * Les PARAMÈTRES sont préfixés `WPR_` pour ne pas entrer en collision avec des
 * noms génériques — `DOMAIN` traîne dans beaucoup d'environnements.
 */
export const CONFIG_KEYS: ConfigKey[] = [
	{
		key: "WPR_PROJECT_NAME",
		required: true,
		section: "Projet",
		comment:
			"Nom lisible du client. Slugifié pour les identifiants (dossier,\npackages, thème, labels Traefik) ; conservé tel quel pour le titre WordPress.",
		example: "Acme Deco",
	},
	{
		key: "WPR_DOMAIN",
		required: true,
		section: "Projet",
		comment: "Domaine du front, SANS schéma. WordPress ira sur wp.<domaine>.",
		example: "acme.example.com",
	},
	{
		key: "WPR_GH_OWNER",
		required: true,
		section: "Projet",
		comment:
			"Propriétaire GitHub du repo client (compte perso ou organisation).",
		example: "acme-org",
	},
	{
		key: "WPR_COOLIFY_SERVER",
		required: true,
		section: "Projet",
		comment:
			"Serveur Coolify : son nom affiché ou son UUID. Son IP sert de cible\naux enregistrements DNS, sauf si WPR_SERVER_IP la remplace.",
		example: "prod-1",
	},
	{
		key: "WPR_SERVER_IP",
		required: false,
		section: "Projet (optionnel)",
		comment:
			"IPv4 publique visée par les enregistrements DNS. Défaut : l'IP que\nCoolify rapporte pour le serveur. À renseigner quand ce serveur est le\n« localhost » d'une instance auto-hébergée : Coolify rapporte alors\nhost.docker.internal, que Cloudflare refuse.",
		example: "203.0.113.42",
	},
	{
		key: "WPR_TARGET_ENV",
		required: false,
		section: "Projet (optionnel)",
		comment:
			"Environnement provisionné : « production » (défaut) ou « staging ».\nIl décide de la branche poussée (main / development), de l'environment\nCoolify (production / development), du préfixe des ressources et de\nl'environment GitHub où atterrissent les variables et les COOLIFY_*_UUID.\nChaque environnement veut son fichier de config, avec ses propres\nWPR_DOMAIN / WPR_WP_HOST — deux environnements ne peuvent pas partager\nun host.",
		example: "staging",
	},
	{
		key: "WPR_BOOTSTRAP",
		required: false,
		section: "Projet (optionnel)",
		comment:
			"« true » sur le TOUT PREMIER run, quel que soit son environnement :\nc'est lui qui scaffolde le repo client, le crée sur GitHub et crée le\nprojet Coolify. À laisser vide ensuite — les runs suivants se greffent\nsur ce qui existe et n'ajoutent que leur environnement.\nLe préflight vérifie la cohérence dans les deux sens : amorcer sur un\nprojet existant échoue, se greffer sur du vide aussi.",
		example: "true",
	},
	{
		key: "WPR_REPO_NAME",
		required: false,
		section: "Projet (optionnel)",
		comment: "Nom du repo GitHub. Défaut : le slug du projet.",
	},
	{
		key: "WPR_WP_HOST",
		required: false,
		section: "Projet (optionnel)",
		comment: "Host WordPress. Défaut : wp.<WPR_DOMAIN>.",
	},
	{
		key: "WPR_NAMESPACE",
		required: false,
		section: "Projet (optionnel)",
		comment: "Namespace des blocs Gutenberg. Défaut : le slug sans tirets.",
	},
	{
		key: "WPR_DIR",
		required: false,
		section: "Projet (optionnel)",
		comment: "Dossier cible. Défaut : le slug, dans le dossier courant.",
	},
	{
		key: "GITHUB_TOKEN",
		required: true,
		section: "Jetons",
		comment: "Portées repo + write:packages.",
	},
	{
		key: "COOLIFY_URL",
		required: true,
		section: "Jetons",
		comment: "URL de l'instance Coolify.",
		example: "https://coolify.example.com",
	},
	{
		key: "COOLIFY_TOKEN",
		required: true,
		section: "Jetons",
		comment:
			"Permissions write + deploy. L'API doit être activée sur l'instance.",
	},
	{
		key: "NODE_AUTH_TOKEN",
		required: true,
		section: "Jetons",
		comment:
			"PAT read:packages. Utilisé par la CI pour installer les @wp-reactor/*.",
	},
	{
		key: "CF_API_TOKEN",
		required: false,
		section: "Cloudflare (optionnel, mais les deux ensemble)",
		comment:
			"Portée Zone:DNS:Edit. Sans la paire CF_API_TOKEN + CF_ZONE_ID, les\nenregistrements DNS sont à créer à la main et la purge du cache edge\nest désactivée.",
	},
	{
		key: "CF_ZONE_ID",
		required: false,
		section: "Cloudflare (optionnel, mais les deux ensemble)",
		comment: "Zone ID du domaine du front.",
	},
]

/**
 * Parseur `.env` minimal, rendant un objet — il ne touche PAS à `process.env`.
 *
 * C'est délibéré et load-bearing : `process.loadEnvFile` n'écrase pas ce qui est
 * déjà dans l'environnement, donc au second passage de la boucle d'attente il ne
 * verrait aucune des valeurs que l'utilisateur vient d'écrire (le premier passage
 * les a déjà posées, vides). Un objet neuf à chaque relecture est la seule façon
 * de faire fonctionner la boucle.
 */
export function parseEnvText(text: string): Record<string, string> {
	const out: Record<string, string> = {}
	for (const rawLine of text.split(/\r?\n/)) {
		const line = rawLine.trim()
		if (!line || line.startsWith("#")) continue

		// `export KEY=value` est toléré : on colle souvent un bloc venu d'un shell.
		const withoutExport = line.startsWith("export ")
			? line.slice("export ".length).trim()
			: line

		const eq = withoutExport.indexOf("=")
		if (eq <= 0) continue

		const key = withoutExport.slice(0, eq).trim()
		let value = withoutExport.slice(eq + 1).trim()

		// Guillemets encadrants seulement : une valeur peut légitimement en
		// contenir (un mot de passe), et `=` aussi — d'où la découpe sur le premier.
		const quote = value[0]
		if (
			(quote === '"' || quote === "'") &&
			value.endsWith(quote) &&
			value.length > 1
		) {
			value = value.slice(1, -1)
		}
		out[key] = value
	}
	return out
}

const SECTION_RULE =
	"# ─────────────────────────────────────────────────────────"

/** Rend le fichier de configuration commenté, toutes valeurs vides. */
export function renderConfigTemplate(): string {
	const lines: string[] = [
		"# Configuration de `create-wp-reactor --provision`.",
		"#",
		"# Remplis les valeurs, puis relance la commande.",
		"#",
		"# ⚠️  CE FICHIER CONTIENT DES JETONS DE PRODUCTION.",
		"#     Il est créé en chmod 600. Ne le committe jamais — ajoute-le à ton",
		"#     .gitignore s'il vit dans un dépôt.",
		"#",
		"# Ce que le shell exporte déjà l'emporte sur ce fichier, et un flag de la",
		"# ligne de commande l'emporte sur les deux.",
		"",
	]

	let currentSection = ""
	for (const entry of CONFIG_KEYS) {
		if (entry.section !== currentSection) {
			currentSection = entry.section
			lines.push(SECTION_RULE, `# ${currentSection}`, SECTION_RULE, "")
		}
		for (const commentLine of entry.comment.split("\n")) {
			lines.push(`# ${commentLine}`)
		}
		if (entry.example) lines.push(`# ex. ${entry.example}`)
		lines.push(`${entry.key}=`, "")
	}

	return lines.join("\n")
}

/** Clés requises absentes ou vides, dans l'ordre du descripteur. */
export function missingKeys(values: Record<string, string>): string[] {
	return CONFIG_KEYS.filter(
		(entry) => entry.required && !values[entry.key]?.trim(),
	).map((entry) => entry.key)
}

/**
 * Incohérences qui ne se voient pas clé par clé.
 *
 * `preflight()` (plan.ts) reste le filet final ; ici on attrape ce qu'on peut
 * expliquer en termes de CLÉS du fichier, pendant que l'utilisateur l'édite.
 */
export function configWarnings(values: Record<string, string>): string[] {
	const warnings: string[] = []
	const hasToken = Boolean(values.CF_API_TOKEN?.trim())
	const hasZone = Boolean(values.CF_ZONE_ID?.trim())
	if (hasToken !== hasZone) {
		warnings.push(
			"CF_API_TOKEN et CF_ZONE_ID vont par paire : renseigne les deux, ou aucun des deux.",
		)
	}
	// Sans ce garde-fou, « prod » ou « prod-1 » retomberait silencieusement sur
	// production — et provisionnerait la prod en croyant viser staging.
	const targetEnv = values.WPR_TARGET_ENV?.trim()
	if (targetEnv && targetEnv !== "production" && targetEnv !== "staging") {
		warnings.push(
			`WPR_TARGET_ENV n'accepte que « production » ou « staging », reçu « ${targetEnv} ».`,
		)
	}

	// `WPR_BOOTSTRAP=1` ou `=yes` passerait pour faux et ferait tenter une greffe
	// sur un projet qui n'existe pas encore.
	const bootstrap = values.WPR_BOOTSTRAP?.trim()
	if (bootstrap && bootstrap !== "true" && bootstrap !== "false") {
		warnings.push(
			`WPR_BOOTSTRAP n'accepte que « true » ou « false », reçu « ${bootstrap} ».`,
		)
	}

	const domain = values.WPR_DOMAIN?.trim()
	if (domain && /^https?:\/\//i.test(domain)) {
		warnings.push("WPR_DOMAIN attend un domaine sans schéma (retire https://).")
	}
	return warnings
}

export interface ResolveInput {
	/** Valeurs lues dans le fichier de configuration. */
	fileValues: Record<string, string>
	/** `process.env`. */
	env: Record<string, string | undefined>
	/** Valeurs venues de la ligne de commande (undefined si absentes). */
	flags: {
		projectName?: string
		dir?: string
		namespace?: string
		domain?: string
		ghOwner?: string
		coolifyServer?: string
		serverIp?: string
		targetEnv?: string
		bootstrap?: boolean
		repoName?: string
		wpHost?: string
		dryRun?: boolean
	}
}

/**
 * Applique la précédence **flag > shell > fichier**.
 *
 * Le shell l'emporte sur le fichier (convention dotenv) pour permettre une
 * surcharge ponctuelle sans éditer le fichier ; le flag l'emporte sur tout, car
 * c'est le plus explicite des trois.
 */
export function resolveProvisionOptions(
	input: ResolveInput,
): Omit<ProvisionOptions, "log" | "fetchImpl"> {
	const { fileValues, env, flags } = input
	const pick = (key: string): string =>
		env[key]?.trim() || fileValues[key]?.trim() || ""

	return {
		projectName: flags.projectName ?? pick("WPR_PROJECT_NAME"),
		// Résolu par l'appelant (il connaît le cwd) quand ni flag ni clé ne le donnent.
		targetDir: flags.dir ?? pick("WPR_DIR"),
		namespace: (flags.namespace ?? pick("WPR_NAMESPACE")) || undefined,
		domain: flags.domain ?? pick("WPR_DOMAIN"),
		githubOwner: flags.ghOwner ?? pick("WPR_GH_OWNER"),
		repoName: (flags.repoName ?? pick("WPR_REPO_NAME")) || undefined,
		coolifyServer: flags.coolifyServer ?? pick("WPR_COOLIFY_SERVER"),
		serverIp: (flags.serverIp ?? pick("WPR_SERVER_IP")) || undefined,
		targetEnv:
			(flags.targetEnv ?? pick("WPR_TARGET_ENV")) === "staging"
				? "staging"
				: "production",
		bootstrap: flags.bootstrap ?? pick("WPR_BOOTSTRAP") === "true",
		wpHost: (flags.wpHost ?? pick("WPR_WP_HOST")) || undefined,
		dryRun: flags.dryRun === true,
		tokens: {
			github: pick("GITHUB_TOKEN"),
			coolifyUrl: pick("COOLIFY_URL"),
			coolifyToken: pick("COOLIFY_TOKEN"),
			nodeAuthToken: pick("NODE_AUTH_TOKEN"),
			cloudflareToken: pick("CF_API_TOKEN") || undefined,
			cloudflareZoneId: pick("CF_ZONE_ID") || undefined,
		},
	}
}

/**
 * Récapitulatif affiché avant confirmation.
 *
 * Aucune valeur de jeton n'y figure — seulement présent/absent. C'est le dernier
 * point d'arrêt avant des appels non idempotents et sans rollback.
 */
export function renderSummary(
	opts: Omit<ProvisionOptions, "log" | "fetchImpl">,
	slug: string,
): string {
	const wpHost = opts.wpHost ?? `wp.${opts.domain}`
	const repo = opts.repoName ?? slug
	const dns =
		opts.tokens.cloudflareToken && opts.tokens.cloudflareZoneId
			? `Cloudflare, zone ${opts.tokens.cloudflareZoneId}`
			: "non géré — enregistrements à créer à la main"

	const tokenState = (label: string, value?: string) =>
		`${label} ${value ? "✓" : "✗"}`

	return [
		"",
		`  Projet      ${opts.projectName}  (slug ${slug})`,
		`  Cible       ${opts.targetEnv}  (branche ${opts.targetEnv === "staging" ? "development" : "main"})`,
		`  Mode        ${
			opts.bootstrap
				? "amorçage — scaffolde le repo, le crée sur GitHub, crée le projet Coolify"
				: "greffe — repo, checkout et projet Coolify attendus déjà présents"
		}`,
		`  Dossier     ${opts.targetDir}`,
		`  Front       https://${opts.domain}`,
		`  WordPress   https://${wpHost}`,
		`  Repo        ${opts.githubOwner}/${repo} (privé)`,
		`  Serveur     ${opts.coolifyServer}${opts.serverIp ? ` (DNS → ${opts.serverIp})` : ""}`,
		`  Coolify     ${opts.tokens.coolifyUrl}`,
		`  DNS         ${dns}`,
		`  Jetons      ${[
			tokenState("GITHUB_TOKEN", opts.tokens.github),
			tokenState("COOLIFY_TOKEN", opts.tokens.coolifyToken),
			tokenState("NODE_AUTH_TOKEN", opts.tokens.nodeAuthToken),
			tokenState("CF_API_TOKEN", opts.tokens.cloudflareToken),
		].join("  ")}`,
		"",
	].join("\n")
}
